import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Form, message } from 'antd';
import EcoLink from 'eco-design/base/eco-link';
import debounce from 'lodash/debounce';
import ReCAPTCHA from 'react-google-recaptcha';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import Modal from './Modal';
import styled from './form.module.scss';
import Button from '@/components/UI/Button';
import { useGlobal } from '@/utils/useGlobal';
import SocialLoginLinks from '@/components/SocailLoginLinks';
import { checkAccountIsExist, createSubscribe, getVerifyCode, toRegisterQuick, toLogin, authSignIn } from '@/service';
import Input from '@/components/UI/Input';
import CountDown from '@/components/CountDown';
import { ENGLISH_LANG, i18nMap, rpcMap } from '@/constants';
import Checkbox from '@/components/UI/Checkbox';
import { getKaamelPlan } from '@/utils/kaamelTest';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { onLogin } from '@/utils/cookie';
import { LOGIN_MAP, FORUM_APPID_MAP } from '@/constants/forum';
import { getCountryRegion } from '@/utils/countryRegion';

type InputFieldType = 'email' | 'code';

interface PasswordlessType {
    visible: boolean;
    onCancel: () => void;
    isRegister?: boolean;
    email?: string;
    isSubscribe?: boolean;
    /** 登录成功后的回调，如果传入则不会跳转页面 */
    onSuccess?: () => void;
}

interface VerifyCodeResponse {
    code: string;
    message?: string;
}

const HOMEBATTERY_REDIRECT = process.env.NEXT_PUBLIC_HOME_BATTERY_DOMAIN;
const RESEND_CODE_COUNTDOWN = 60;
const WORDPRESS_SITE = process.env.NEXT_PUBLIC_WORDPRESS_SITE;
const WEB_HOST = process.env.NEXT_PUBLIC_WEB_HOST;

message.config({
    duration: 10,
    top: 150,
    getContainer: () => document.body,
    maxCount: 3,
});

const Passwordless = ({ visible, onCancel, isRegister = false, email = '', isSubscribe = false, onSuccess }: PasswordlessType) => {
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;

    const recaptchaRef = useRef();
    const { useLang, useRouter, gtm, Link } = useGlobal();
    const router = useRouter();

    const { t } = useLang('shared.global_login');
    const { locale, query } = router;

    const [form] = Form.useForm();
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];

    const [kaamelInfo, setKaamelInfo] = useState<any>(null);
    const [isNewUser, setIsNewUser] = useState(isRegister);
    const [resendCode, setResendCode] = useState(0);
    const [sendCodeLoading, setSendCodeLoading] = useState(false);
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const [errorTip, setErrorTip] = useState('');
    const [isErrorTip, setIsErrorTip] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [emailValidating, setEmailValidating] = useState<'' | 'success' | 'warning' | 'error' | 'validating'>('');
    const [activeFields, setActiveFields] = useState<Record<InputFieldType, boolean>>({
        email: false,
        code: false,
    });
    const [allValidate, setAllValidate] = useState({
        email: false,
        code: false,
    });

    const portalRoot = document.getElementById('recaptcha-portal');

    let open_site: string | undefined = '';
    switch (getCountryRegion(locale)) {
        case 'cn':
            open_site = process.env.NEXT_PUBLIC_OPEN_SITE_CN;
            break;
        case 'eu':
            open_site = process.env.NEXT_PUBLIC_OPEN_SITE_EU;
            break;
        default:
            open_site = process.env.NEXT_PUBLIC_OPEN_SITE;
            break;
    }

    const redirect = typeof window !== 'undefined' ? window.location.href : router.asPath;

    const registerUrl = useMemo(() => {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : router.asPath;
        return `/${locale}/register?redirect=${encodeURIComponent(currentUrl)}`;
    }, [locale, router.asPath]);

    const loginUrl = useMemo(() => {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : router.asPath;
        return `/${locale}/login?redirect=${encodeURIComponent(currentUrl)}`;
    }, [locale, router.asPath]);

    const buildAgreementUrl = useCallback((type: 'privacy' | 'terms', locale: string, kaamelInfo?: any) => {
        const baseUrl = 'https://account.ecoflow.com/agreement';

        if (locale === 'cis') {
            return `${baseUrl}/ru-cis/${type === 'privacy' ? 'PrivacyPolicy' : 'TermsOfUse'}.html`;
        }

        if (locale === 'uk' && type === 'privacy' && kaamelInfo?.privacyUrl) {
            return kaamelInfo.privacyUrl;
        }

        const langCode = i18nMap?.[locale] || i18nMap?.us || 'us';
        return `${baseUrl}/${langCode}/${type === 'privacy' ? 'PrivacyPolicy' : 'TermsOfUse'}.html`;
    }, []);

    const handleAgreementChange = useCallback(
        (e: CheckboxChangeEvent) => {
            const isAgreed = e?.target?.checked ? 'Agree' : 'Disagree';
            gtm?.push({
                event: 'uaEvent',
                event_name: 'register_privacy_agree',
                tag: isAgreed,
            });
        },
        [gtm],
    );

    const handle_GTM_319 = useCallback(
        (e: CheckboxChangeEvent) => {
            const tag = e?.target?.checked ? 'Agree' : 'Disagree';
            gtm?.push({
                event: 'uaEvent',
                event_name: 'subscription_agree',
                tag,
            });
            subscribeCookie({
                entry_name: 'Passwordfree entry',
            });
        },
        [gtm],
    );

    const AgreementLinks = useMemo(() => {
        const privacyUrl = buildAgreementUrl('privacy', locale, kaamelInfo);
        const termsUrl = buildAgreementUrl('terms', locale, kaamelInfo);

        return {
            link1: (children: string) => (
                <Link href={privacyUrl} newWin>
                    {children}
                </Link>
            ),
            link2: (children: string) => (
                <Link href={termsUrl} newWin>
                    {children}
                </Link>
            ),
        };
    }, [buildAgreementUrl, locale, kaamelInfo, Link]);

    const updateEmailValidation = useCallback((isValid: boolean, status: '' | 'success' | 'warning' | 'error' | 'validating') => {
        setEmailValidating(status);
        setAllValidate((prev) => ({ ...prev, email: isValid }));
    }, []);

    const resetErrorState = useCallback(() => {
        setIsErrorTip(false);
        setErrorTip('');
    }, []);

    const setErrorState = useCallback((message: string) => {
        setIsErrorTip(true);
        setErrorTip(message);
    }, []);

    const onBlurInput = useCallback((event: InputEvent, fieldType: InputFieldType) => {
        if (!(event?.target as HTMLInputElement)?.value) {
            setActiveFields((prev) => ({
                ...prev,
                [fieldType]: false,
            }));
        }
    }, []);

    const onFocusInput = useCallback((event: InputEvent, fieldType: InputFieldType) => {
        if (!(event?.target as HTMLInputElement)?.value) {
            setActiveFields((prev) => ({
                ...prev,
                [fieldType]: true,
            }));
        }
    }, []);

    const validateEmail = useCallback(() => {
        return {
            validator: async (_: unknown, value: string) => {
                const EMAIL_REGEX = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;

                if (!value) {
                    updateEmailValidation(false, 'error');
                    return Promise.reject(t('site_login_tip_email_empty'));
                }

                if (!EMAIL_REGEX.test(value)) {
                    updateEmailValidation(false, 'error');
                    return Promise.reject(t('site_login_tip_email_format_incorrect'));
                }

                setEmailValidating('validating');

                try {
                    const res: any = await checkAccountIsExist({ email: value });

                    setEmailValidating('success');

                    if (res.code === '0') {
                        setIsNewUser(false);
                        // updateEmailValidation(false, 'error');
                        // return Promise.reject(t('site_login_tip_email_be_registered'));
                    } else if (res.code === '1003') {
                        setIsNewUser(true);
                    }

                    updateEmailValidation(true, 'success');
                    return Promise.resolve();
                } catch (err) {
                    updateEmailValidation(false, '');
                }
            },
        };
    }, [updateEmailValidation, t]);

    const handleSendVerificationCode = async () => {
        await form.validateFields(['email', 'recaptchaResponse']);

        try {
            setSendCodeLoading(true);
            resetErrorState();

            const account = form.getFieldValue('email');
            const requestData = {
                scene: isNewUser ? 'WEBSITE_REGISTER_VERIFICATION_CODE' : 'WEBSITE_LOGIN_VERIFICATION_CODE',
                account,
                recaptchaResponse,
            };

            const res: VerifyCodeResponse = await getVerifyCode(requestData, locale);

            if (res.code === '0') {
                setResendCode(RESEND_CODE_COUNTDOWN);
                resetErrorState();
            } else {
                res.message && setErrorState(res.message);
            }
        } catch (err) {
            console.error('发送验证码失败:', err);
        } finally {
            setSendCodeLoading(false);
        }
    };

    const validPrePath = (path: string) => {
        if (typeof window !== 'undefined') {
            const origin = (window as any).location.origin;
            return (path.includes(origin) && path) || '';
        }
        return '';
    };

    const onReCAPTCHAChange = async (captchaCode: string) => {
        console.log({ captchaCode });
        if (!captchaCode) return;
        setRecaptchaResponse(captchaCode);
        form.setFields([
            {
                name: 'recaptchaResponse',
                value: captchaCode,
                errors: [],
            },
        ]);
    };

    const formResetFunc = () => {
        form.resetFields();

        setEmailValidating('');
        setAllValidate({
            email: false,
            code: false,
        });
        setActiveFields({
            email: false,
            code: false,
        });
        setResendCode(0);
        setErrorTip('');
        setIsErrorTip(false);
    };

    const goAfterLogin = () => {
        // 如果传入了 onSuccess 回调，则不跳转页面，调用回调
        if (onSuccess) {
            onSuccess();
            return;
        }
        const url = redirect ? redirect : `/${locale}/my-overview`;
        signupCookie({
            entry_name: 'Create EcoFlow Account_Register',
        });
        window.location.href = url;
    };

    const getShopfiyReturnToNew = (url: string) => {
        const homePath = `/${router?.locale || ''}`;
        const prevUrlBalckList = ['/login', '/register', '/validate', '/forget'];

        if (typeof window === 'undefined') return homePath;

        let { redirect } = router.query;

        const origin = window.location.origin;
        if (redirect && !prevUrlBalckList.some((item: any) => redirect.includes(item))) {
            if (!redirect.startsWith('http') && !redirect.startsWith('www.')) {
                redirect = `${origin}${redirect.startsWith('/') ? redirect : `/${redirect}`}`;
                return decodeURIComponent(`${redirect}`);
            }
            if ((redirect.startsWith('http') || redirect.startsWith('www.')) && !redirect.includes('.ecoflow.com')) {
                redirect = `${origin}${homePath}`;
                return decodeURIComponent(`${redirect}`);
            }
            return decodeURIComponent(`${redirect}`);
        }

        const prevPath = typeof window !== 'undefined' ? document.referrer : '';

        let prevRoute = validPrePath(prevPath);
        if (prevRoute && !prevUrlBalckList.some((item: any) => prevRoute.includes(item))) {
            if (!prevRoute.startsWith('http') && !prevRoute.startsWith('www.')) {
                prevRoute = `${origin}${prevRoute.startsWith('/') ? prevRoute : `/${prevRoute}`}`;
            }
            if ((prevRoute.startsWith('http') || prevRoute.startsWith('www.')) && !prevRoute.includes('.ecolfow.com')) {
                prevRoute = `${origin}${homePath}`;
            }
            return decodeURIComponent(`${prevRoute}`);
        }
        return url || decodeURIComponent(`${origin}${homePath}`);
    };

    const syncLoginShopify = async (url: string): Promise<string> => {
        return new Promise(async (resolve, reject) => {
            let returnToNew = getShopfiyReturnToNew(url);
            if (returnToNew) {
                returnToNew = returnToNew.startsWith('http') || returnToNew.startsWith('www.') ? returnToNew : `${WEB_HOST}${returnToNew}`;
            } else {
                returnToNew = `${WEB_HOST}/${locale}`;
            }
            const params = {
                returnToNew,
                url: `https://${router?.locale}.ecoflow.com`,
            };
            const res = await authSignIn(params, router?.locale);
            if (res?.code !== '0') {
                reject(res?.message);
            } else {
                resolve(res?.data || `/${router.locale}`);
            }
        });
    };

    const handleNewUserRegister = async () => {
        try {
            const data = form.getFieldsValue();
            const { email, code, remember, subscribe } = data;
            const recaptchaRes = { recaptchaResponse };
            const { _source, scene, inviteCode } = query;

            const redirect = query?.redirect ? query?.redirect : window.location.href;

            setSubmitLoading(true);

            if (isNewUser) {
                const params: any = {
                    source: 'ECOFLOW_WEB',
                    email: email,
                    userType: 'ECOFLOW', //用户类型
                    scene: 'ECOFLOW_WEB', // 登陆场景
                    isAgree: remember,
                    code,
                    ...recaptchaRes,
                    entry_name: 'Create EcoFlow Account',
                };
                if (inviteCode) {
                    Object.assign(params, { inviteCode2: inviteCode });
                }
                const res: any = await toRegisterQuick(params, '', locale);
                setSubmitLoading(false);

                if (res.code === '0') {
                    message.success(t('site_register_successful'));

                    onCancel();
                    formResetFunc();

                    setRecaptchaResponse('');
                    const rptRef: any = recaptchaRef?.current;
                    rptRef?.reset && rptRef?.reset();
                    onLogin(res.data, false);

                    if (locale === 'us' || locale === 'ca' || subscribe) {
                        await createSubscribe({
                            email: email,
                            source: 'WEB_REGISTER',
                            eventTracking: subscribeCookie({
                                entry_name: 'Create EcoFlow Account',
                            }),
                        });
                    }

                    setTimeout(() => {
                        goAfterLogin();
                    }, 1000);
                } else {
                    setErrorTip(res.message || 'error');
                    setIsErrorTip(true);
                }
            } else {
                const params = {
                    source: LOGIN_MAP[_source] || 'ECOFLOW_WEB',
                    userType: 'ECOFLOW',
                    scene: LOGIN_MAP[_source] || 'ECOFLOW_WEB',
                    logInSource: 1,
                    email: email,
                    verificationCode: code,
                    ...recaptchaRes,
                };
                const decodeRedirect = decodeURIComponent(redirect);
                const res: any = await toLogin(params, '', _source === LOGIN_MAP.ECOFLOW_WEB_COMMUNITY ? FORUM_APPID_MAP[locale] : '');
                setSubmitLoading(false);
                if (res.code === '0') {
                    const IS_HOME_BATTERY = redirect.indexOf('https://homebattery') > -1 || redirect.indexOf('https://enterprise') > -1;
                    onLogin(res.data, IS_HOME_BATTERY);

                    if (res.data?.user?.destroyed === 'DESTROY') {
                        window.location.href = `/${router.locale || ''}/my-account-manage`;
                        return;
                    }

                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'login',
                        page_name: '/login',
                    });

                    onCancel();

                    // 如果传入了 onSuccess 回调，则不跳转页面，调用回调
                    if (onSuccess) {
                        onSuccess();
                        return;
                    }

                    if (redirect && scene) {
                        if (redirect.includes(HOMEBATTERY_REDIRECT)) {
                            window.location.href = decodeRedirect;
                            return;
                        }
                        window.location.href = `${WORDPRESS_SITE}?returnTo=${decodeRedirect}&token=${res.data.token}`;
                        return;
                    }

                    if (window.opener) {
                        window.opener.postMessage(
                            {
                                userInfo: res.data?.user,
                                token: res.data.token,
                            },
                            open_site,
                        );
                    }

                    try {
                        const res2 = await syncLoginShopify(redirect);
                        window.location.href = String(res2);
                    } catch (err) {
                        window.location.href = `/${router.locale || ''}`;
                    } finally {
                        setSubmitLoading(false);
                    }
                } else {
                    setSubmitLoading(false);
                    setErrorTip(res.message);
                    setIsErrorTip(true);

                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'login_fail',
                        page_name: '/login',
                    });
                }
            }
        } catch (error) {
            setSubmitLoading(false);
            console.error(error);
        }
    };

    useEffect(() => {
        const info = getKaamelPlan();
        setKaamelInfo(info);
        if (!visible) {
            // resetForm();
            const info = getKaamelPlan();
            setKaamelInfo(info);
        }
    }, [visible]);

    useEffect(() => {
        if (isSubscribe && email) {
            form.setFieldsValue({ email });
            form.validateFields(['Email']);
            setAllValidate({ ...allValidate, email: true });
        }
    }, [isSubscribe, email]);

    return (
        <Modal className={styled.formContent} visible={visible} onClose={onCancel} title={t('modal_title_register')}>
            <div>
                <Form name="normal_login" className="login-form" form={form} initialValues={{ remember: true }} onFinish={handleNewUserRegister}>
                    <div className="inputItem">
                        {/* <label className={activeFields.email ? 'active' : ''}>{t('site_login_email')}</label> */}
                        <Form.Item name="email" validateStatus={emailValidating} rules={[validateEmail]} validateTrigger="onBlur">
                            <Input
                                className="default-input "
                                autoComplete="off"
                                aria-label={t('site_login_email')}
                                // onFocus={(e: InputEvent) => onFocusInput(e, 'email')}
                                // onBlur={(e: InputEvent) => onBlurInput(e, 'email')}
                                placeholder={t('site_login_email')}
                                onChange={debounce(() => {
                                    if (!activeFields.email) {
                                        setActiveFields({
                                            email: true,
                                            code: activeFields.code,
                                        });
                                        form.validateFields(['Email']);
                                    }
                                }, 300)}
                            />
                        </Form.Item>
                    </div>
                    <div className="inputItem">
                        {/* <label className={activeFields.code ? 'active' : ''}>{t('site_register_enter_code')}</label> */}
                        <Form.Item name="code" rules={[{ required: true, message: t('site_register_enter_code_tip') }]}>
                            <Input
                                className="default-input verify-code"
                                autoComplete="off"
                                aria-label={t('site_register_enter_code')}
                                maxLength={6}
                                placeholder={t('site_register_enter_code')}
                                // onFocus={(e: InputEvent) => onFocusInput(e, 'code')}
                                // onBlur={(e: InputEvent) => onBlurInput(e, 'code')}
                                suffix={
                                    <Button
                                        type="link"
                                        className={`send-code-btn ${!allValidate.email || resendCode > 0 ? 'disabled' : ''}`}
                                        loading={sendCodeLoading}
                                        disabled={!allValidate.email || resendCode > 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendVerificationCode();
                                        }}
                                    >
                                        {resendCode > 0 ? <CountDown sec={resendCode} secUnit="s" onFinish={() => setResendCode(0)} /> : t('site_get_code')}
                                    </Button>
                                }
                            />
                        </Form.Item>
                    </div>
                    <div className="inputItem">
                        <Form.Item
                            name="recaptchaResponse"
                            rules={[
                                {
                                    validator(_, value) {
                                        if (value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(t('site_login_tip_perform_verification'));
                                    },
                                },
                            ]}
                            style={{ marginBottom: '20px', marginTop: '16px' }}
                        >
                            {/* @ts-ignore */}
                            <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                        </Form.Item>
                        {isErrorTip && <div className="error-tip">{errorTip}</div>}
                    </div>
                    <Button
                        type="primary"
                        htmlType="submit"
                        className="register-form-submit"
                        loading={submitLoading}
                        aria-label={t('button_name_register')}
                        disabled={!allValidate.email}
                    >
                        {t('button_name_register')}
                    </Button>
                    {isNewUser && !['us', 'ca'].includes(locale) && (
                        <Form.Item className="check">
                            <Form.Item name="subscribe" valuePropName="checked" noStyle>
                                <Checkbox className="agreement" onChange={handle_GTM_319}>
                                    <span className="checkbox-msg">{t('site_register_agree_subscribe')}</span>
                                </Checkbox>
                            </Form.Item>
                        </Form.Item>
                    )}
                    {isNewUser && (
                        <Form.Item>
                            <Form.Item
                                name="remember"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) => (value ? Promise.resolve() : Promise.reject(t('site_login_tip_agree_user_agreement1'))),
                                    },
                                ]}
                                noStyle
                            >
                                <Checkbox className="agreement" onChange={handleAgreementChange}>
                                    <span className="checkbox-msg">{t.rich('site_login_tip_agree_user_agreement', AgreementLinks)}</span>
                                </Checkbox>
                            </Form.Item>
                        </Form.Item>
                    )}
                </Form>
                {isNewUser && (
                    <div className="global_login_redirect">
                        {t.rich('redirect_link', {
                            link: (text: string) => (
                                <EcoLink className="global_login_redirect_link" href={registerUrl}>
                                    {text}
                                </EcoLink>
                            ),
                        })}
                    </div>
                )}
                <div className="divider-line">{`- ${t('site_login_or')} -`}</div>
                <div className="social-content">
                    <SocialLoginLinks redirectUrl={redirect} showLineLogin={locale === 'jp'} />
                </div>
            </div>
        </Modal>
    );
};

export default Passwordless;
